<template>
  <div class="scan-mode-switch" role="radiogroup" aria-label="Mode de saisie">
    <button v-for="option in options" :key="option.value" type="button" role="radio"
      class="scan-mode-option" :class="{ 'is-active': option.value === value }"
      :aria-checked="option.value === value ? 'true' : 'false'" @click="select(option.value)">
      <i :class="option.icon" aria-hidden="true"></i>
      <span>{{ option.text }}</span>
    </button>
  </div>
</template>

<script>
/**
 * QR-004 / QR-006 : sélecteur du mode de saisie des écrans de scan (douchette, appareil
 * photo, saisie manuelle).
 *
 * Il se lit comme un interrupteur à trois positions, pas comme trois boutons d'action :
 * rail neutre, position courante détachée en blanc — le vocabulaire des onglets du thème.
 * Les boutons pleins de Bootstrap faisaient du mode courant un appel à l'action, plus
 * voyant que le champ de scan qu'il ne fait que configurer.
 *
 * Chaque position porte une icône : le préparateur reconnaît le geste avant de lire le
 * libellé, et les trois restent lisibles sur un téléphone de 360 px, là où les libellés
 * en capitales espacées débordaient.
 *
 * Composant partagé par tous les écrans de scan, pour que le geste soit le même partout.
 * Il ne fait que refléter `scanMode` (v-model) : les effets d'un changement de mode
 * restent au mixin scannedValue.
 */
import { SCAN_MODE_SCAN, SCAN_MODE_CAMERA, SCAN_MODE_MANUAL } from "@/mixins/scannedValue";

export default {
  name: "ScanModeSwitch",
  props: {
    value: {
      type: String,
      required: true
    }
  },
  computed: {
    options() {
      return [
        { value: SCAN_MODE_SCAN, text: "Scan", icon: "far fa-barcode-read" },
        { value: SCAN_MODE_CAMERA, text: "Photo", icon: "far fa-camera" },
        { value: SCAN_MODE_MANUAL, text: "Saisie manuelle", icon: "far fa-keyboard" }
      ];
    }
  },
  methods: {
    select(mode) {
      // Re-sélectionner le mode courant ne doit pas repasser par le watcher du mixin, qui
      // viderait le champ en cours de saisie.
      if (mode !== this.value) {
        this.$emit("input", mode);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

.scan-mode-switch {
  display: flex;
  padding: 3px;
  background-color: $bg-1;
}

// Positions de largeur égale, icône au-dessus du libellé : « Saisie manuelle » tient
// ainsi sur une ligne sans écraser « Scan » et « Photo », et la cible tactile dépasse
// les 44 px recommandés.
.scan-mode-option {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 2px;
  border: none;
  border-radius: 0;
  background: none;
  // $text-50p ne ressort qu'à 2,5:1 sur le rail : trop pâle pour un écran lu en entrepôt.
  color: darken($text-50p, 18%);
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s, background-color 0.15s, box-shadow 0.15s;
  @extend .clanprobold;

  i {
    font-size: 18px;
    margin-bottom: 5px;
  }

  &:focus {
    outline: none;
  }

  // Focus clavier seulement : au doigt, l'anneau resterait accroché après le tap.
  &:focus-visible {
    box-shadow: inset 0 0 0 2px rgba($primary, 0.5);
  }

  &:active:not(.is-active) {
    background-color: rgba(#fff, 0.5);
  }

  // Même détachement que l'onglet actif du thème (.nav-tabs).
  &.is-active {
    background-color: #fff;
    color: $primary-darker;
    box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.1);

    i {
      color: $primary;
    }
  }
}

// Petits téléphones : un point de moins, pour que « Saisie manuelle » ne touche pas les bords.
@media (max-width: 360px) {
  .scan-mode-option {
    font-size: 10px;
  }
}

// À la souris, le sélecteur se range à gauche du champ : icône et libellé côte à côte,
// à la hauteur du champ plutôt qu'empilés.
@media (min-width: 769px) {
  // Le rail épouse ses trois positions, au lieu de s'étirer en aplat gris sur toute la ligne.
  .scan-mode-switch {
    display: inline-flex;
    vertical-align: top;
  }

  .scan-mode-option {
    flex: 0 0 auto;
    flex-direction: row;
    // 40 px avec le rail, comme le champ (.form-control du thème).
    padding: 10px 14px;
    font-size: 12px;

    i {
      font-size: 14px;
      margin: 0 7px 0 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Global/ScanModeSwitch.vue